// El menú de colores del compositor — ADR-2026-09-21-el-numeral-abre-colores y
// ADR-2026-09-21-pantone-en-el-menu-de-colores. Escribir `#` en la caja del Agente abre un
// menú con los últimos usados, los básicos, los pastel, algunos Pantone (con búsqueda en
// la tabla de aproximaciones) y "Nuevo color" (el picker del navegador); elegir uno deja
// su hex en el texto (y, si es Pantone, el código y el nombre entre paréntesis). Todo PURO
// (el storage se inyecta) para poder testear la detección del token, el filtro y el
// reemplazo, que es donde se esconden los off-by-one.

import { PANTONE, PANTONE_DESTACADOS, buscarPantone, pantonePorHex, pareceCodigoPantone, rotuloDePantone } from './pantone';

export interface ColorDelChat {
  hex: string;
  nombre: string;
}

export const COLORES_BASICOS: readonly ColorDelChat[] = [
  { hex: '#000000', nombre: 'Negro' },
  { hex: '#FFFFFF', nombre: 'Blanco' },
  { hex: '#8E8E93', nombre: 'Gris' },
  { hex: '#F5F0E6', nombre: 'Crudo' },
  { hex: '#1644EB', nombre: 'Azul' }, // hex-de-marca-ok: paleta que elige el usuario
  { hex: '#0F2A5F', nombre: 'Azul marino' },
  { hex: '#FF2D2D', nombre: 'Rojo' },
  { hex: '#7A1F2B', nombre: 'Bordó' },
  { hex: '#FF8A00', nombre: 'Naranja' },
  { hex: '#FFD60A', nombre: 'Amarillo' },
  { hex: '#34C759', nombre: 'Verde' },
  { hex: '#3A5A40', nombre: 'Verde oliva' },
  { hex: '#FF6FAE', nombre: 'Rosa' },
  { hex: '#7B3FE4', nombre: 'Violeta' },
  { hex: '#8B5A2B', nombre: 'Marrón' },
];

export const COLORES_PASTEL: readonly ColorDelChat[] = [
  { hex: '#F8C8DC', nombre: 'Rosa pastel' },
  { hex: '#FFDAB9', nombre: 'Durazno' },
  { hex: '#FFB7B2', nombre: 'Salmón pastel' },
  { hex: '#FDFD96', nombre: 'Amarillo pastel' },
  { hex: '#FFF8DC', nombre: 'Crema' },
  { hex: '#F5E6CC', nombre: 'Beige' },
  { hex: '#B5EAD7', nombre: 'Menta' },
  { hex: '#C1E1C1', nombre: 'Verde pastel' },
  { hex: '#A0E7E5', nombre: 'Turquesa pastel' },
  { hex: '#AEC6CF', nombre: 'Celeste pastel' },
  { hex: '#BFD7EA', nombre: 'Azul bebé' },
  { hex: '#C3B1E1', nombre: 'Lila' },
  { hex: '#E6E6FA', nombre: 'Lavanda' }, // hex-de-marca-ok: color que elige el usuario
  { hex: '#E5E4E2', nombre: 'Gris perla' },
];

export const CLAVE_COLORES_RECIENTES = 'atlas-chat-atlas-colores-recientes';
export const MAX_RECIENTES = 8;

const HEX = /^#[0-9a-f]{6}$/i;

/** `#rgb` o `#rrggbb` (con o sin numeral) como `#RRGGBB`; `null` si no es un color. */
export function normalizarHex(raw: string): string | null {
  let s = raw.trim();
  if (!s.startsWith('#')) s = `#${s}`;
  if (/^#[0-9a-f]{3}$/i.test(s)) s = `#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}`;
  return HEX.test(s) ? s.toUpperCase() : null;
}

type Storage = Pick<globalThis.Storage, 'getItem' | 'setItem'>;

export function leerColoresRecientes(storage: Storage | null | undefined): string[] {
  try {
    const raw = storage?.getItem(CLAVE_COLORES_RECIENTES);
    if (!raw) return [];
    const lista: unknown = JSON.parse(raw);
    if (!Array.isArray(lista)) return [];
    const out: string[] = [];
    for (const x of lista) {
      const hex = typeof x === 'string' ? normalizarHex(x) : null;
      if (hex && !out.includes(hex)) out.push(hex);
      if (out.length >= MAX_RECIENTES) break;
    }
    return out;
  } catch {
    return [];
  }
}

/** El color recién usado adelante, sin repetir, acotado a `MAX_RECIENTES`. */
export function conColorReciente(lista: readonly string[], hex: string): string[] {
  return [hex, ...lista.filter((h) => h !== hex)].slice(0, MAX_RECIENTES);
}

/** Los colores `#RRGGBB` (o `#RGB`) que hay en un texto, normalizados y sin repetir, en el
 *  orden en que aparecen: lo que se manda con un color escrito o pegado a mano también
 *  cuenta como usado. */
export function hexesEn(texto: string): string[] {
  const out: string[] = [];
  for (const m of texto.matchAll(/(^|[^\p{L}\p{N}#])#([0-9a-f]{6}|[0-9a-f]{3})(?![\p{L}\p{N}])/giu)) {
    const hex = normalizarHex(m[2]);
    if (hex && !out.includes(hex)) out.push(hex);
  }
  return out;
}

/** Varios colores usados de una vez (un mensaje con dos o tres): el último del texto queda primero. */
export function conColoresRecientes(lista: readonly string[], hexes: readonly string[]): string[] {
  return hexes.reduce<string[]>((acc, h) => conColorReciente(acc, h), [...lista]);
}

export function guardarColoresRecientes(storage: Storage | null | undefined, lista: readonly string[]): void {
  try {
    storage?.setItem(CLAVE_COLORES_RECIENTES, JSON.stringify(lista));
  } catch {
    /* sin storage no hay recientes: no pasa nada */
  }
}

export interface TokenNumeral {
  /** Índice del `#` en el texto. */
  desde: number;
  /** Lo escrito después del `#`, hasta el cursor. */
  consulta: string;
}

/**
 * El token `#...` sobre el que está el cursor, si lo hay: un `#` al principio o después
 * de un espacio o salto, seguido sólo de letras/números hasta el cursor. `#` pegado a una
 * palabra ("nro#3") o con un espacio en el medio no cuenta: el menú se cierra solo.
 */
export function tokenNumeralEn(texto: string, cursor: number): TokenNumeral | null {
  const hasta = Math.max(0, Math.min(cursor, texto.length));
  const antes = texto.slice(0, hasta);
  const m = /(^|[\s(])#([\p{L}\p{N}]*)$/u.exec(antes);
  if (!m) return null;
  const desde = hasta - m[2].length - 1;
  return { desde, consulta: m[2] };
}

export type SeccionDeColor = 'recientes' | 'basicos' | 'pastel' | 'pantone' | 'busqueda';

export interface OpcionDeColor extends ColorDelChat {
  seccion: SeccionDeColor;
  /** Un Pantone: su código, que se ve al lado del nombre. */
  codigo?: string;
  /** Lo que va en el mensaje después del hex ("(Pantone 19-4052 TCX Classic Blue)"). */
  sufijo?: string;
}

/** El menú "normal" o buscando en la tabla Pantone entera. */
export type ModoDelMenu = 'colores' | 'pantone';

const sinAcentos = (s: string): string => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const opcionPantone = (c: { codigo: string; nombre: string; hex: string }, seccion: SeccionDeColor): OpcionDeColor => ({
  hex: c.hex,
  nombre: c.nombre,
  codigo: c.codigo,
  sufijo: rotuloDePantone(c),
  seccion,
});

/** El nombre con el que se muestra un hex suelto (un reciente): el de la paleta o la tabla Pantone si está, si no el hex. */
function opcionDeHex(hex: string, seccion: SeccionDeColor): OpcionDeColor {
  const basico = COLORES_BASICOS.find((c) => c.hex === hex) ?? COLORES_PASTEL.find((c) => c.hex === hex);
  if (basico) return { ...basico, seccion };
  const pantone = pantonePorHex(hex);
  if (pantone) return opcionPantone(pantone, seccion);
  return { hex, nombre: hex, seccion };
}

/**
 * Las opciones del menú en el orden en que se ven. En modo `colores`: los recientes
 * primero, después básicos, pastel y los Pantone destacados, sin repetir los que ya
 * salieron; la consulta filtra por nombre, código o hex, y si parece un código Pantone
 * busca también en la tabla entera. En modo `pantone`: sólo la tabla, filtrada.
 */
export function opcionesDeColor(consulta: string, recientes: readonly string[], modo: ModoDelMenu = 'colores'): OpcionDeColor[] {
  const q = sinAcentos(consulta.trim());
  const qHex = q.replace(/^#/, '');
  if (modo === 'pantone') return buscarPantone(consulta).map((c) => opcionPantone(c, 'busqueda'));
  const pasa = (c: OpcionDeColor): boolean =>
    !q ||
    sinAcentos(c.nombre).includes(q) ||
    (!!c.codigo && sinAcentos(c.codigo).replace(/[\s-]+/g, '').includes(q.replace(/[\s-]+/g, ''))) ||
    (qHex.length > 0 && c.hex.slice(1).toLowerCase().startsWith(qHex));
  const out: OpcionDeColor[] = [];
  const vistos = new Set<string>();
  const sumar = (o: OpcionDeColor) => {
    if (vistos.has(o.hex) || !pasa(o)) return;
    vistos.add(o.hex);
    out.push(o);
  };
  for (const hex of recientes) sumar(opcionDeHex(hex, 'recientes'));
  for (const c of COLORES_BASICOS) sumar({ ...c, seccion: 'basicos' });
  for (const c of COLORES_PASTEL) sumar({ ...c, seccion: 'pastel' });
  for (const codigo of PANTONE_DESTACADOS) {
    const c = PANTONE.find((x) => x.codigo === codigo);
    if (c) sumar(opcionPantone(c, 'pantone'));
  }
  // Un código escrito a mano ("#19-4052", "#186c") se busca en la tabla entera sin
  // tener que entrar a "Buscar color Pantone".
  if (pareceCodigoPantone(consulta)) for (const c of buscarPantone(consulta, 10)) sumar(opcionPantone(c, 'busqueda'));
  return out;
}

/** El texto con el token reemplazado por el hex (y el sufijo, si lo hay: el código Pantone)
 *  más un espacio detrás (el que ya había, si había), y el cursor después de ese espacio,
 *  listo para seguir escribiendo. */
export function conColorInsertado(texto: string, token: TokenNumeral, hex: string, sufijo = ''): { texto: string; cursor: number } {
  const hasta = token.desde + 1 + token.consulta.length;
  const resto = texto.slice(hasta);
  const pegado = resto.startsWith(' ') ? '' : ' ';
  const insertado = sufijo ? `${hex} ${sufijo}` : hex;
  const nuevo = `${texto.slice(0, token.desde)}${insertado}${pegado}${resto}`;
  return { texto: nuevo, cursor: token.desde + insertado.length + 1 };
}
